import * as XLSX from 'xlsx-js-style';
import { getCopy } from './copy';
import { Row } from '../interfaces';
import { LocalDate } from "../../../core/component/pojo/date-variable";
import { DateService } from "../../../util/common/date.service";

type Primitives = string | number | boolean | null | undefined;
export type PartialCell = Partial<XLSX.CellObject>;
export type DataType = Primitives | PartialCell;

/**
 * Different format for cells.
 */
export const formats = {
  thousandsSeparator: '#,##0_-;-* #,##0_-;_-* "-"??_-;_-@_-',
  thousandsSeparatorSimple: '#,##0',
  multiplier: 'x0.00',
  multiplierWithDash: '[>0]x0.00;[<0]x0.00;"-"',
  percents: '0.00%',
  percentsWithDash: '0.00%;-0.00%;"-";@',
  percentsNoSpace: '0.00%',
  percentsRounded: '0%',
  toFixed2: '0.00',
  toFixed4: '0.0000',
  date: 'mmm dd, yyyy',
  year: 'yyyy',
  dashMultiplier: '[>0]x0.00;[<0]x0.00;-',
  emptyMultiplier: '[>0]x0.00;[<0]x0.00;""',
  thousandsSeparatorToFixed2: '#,##0.00_-;-* #,##0.00_-;_-* "-"??_-;_-@_-',
};

/**
 * Use just for more convenient cell creation
 *
 * @param data Partial cell object or any non-object.
 * @returns cell object.
 */
export const getCell = (data: DataType): PartialCell => {
  if (typeof data !== 'object') return { v: data };
  return getCopy(data);
};

/**
 * Add styles to cell.
 *
 * @param data Primitive or partial cell object.
 * @param style Styles to add.
 * @returns styled cell object.
 */
export const withCellStyles = (data: DataType, style: XLSX.CellStyle): PartialCell => {
  if (data === null) {
    return;
  }
  let copy: PartialCell;
  if (typeof data !== 'object') copy = getCell(data);
  copy = copy || data as PartialCell;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  copy.s = { ...copy.s, ...style };
  return copy;
};

/**
 * After the Excel import, headers might have not a standard data format (e.x. instead of string, it is number or date).
 * Some formats create spaces before and after the header name: ' Investment '.
 * This function does take an array of objects and trims the object keys as follows:
 * [ {' Name ': 'Alejandro', age: 34} ]
 *                   ↓
 * [ { Name: 'Alejandro', age: 34} ]
 *
 * @param data is an array of Excel rows.
 */
export const trimExcelHeaders = (data: unknown[]): Row[] => {
  if (!data) {
    return;
  }
  const parsedData = [];
  data.forEach((row) => {
    const parsedRow = {};
    const keys = Object.keys(row);
    const values = Object.values(row);
    for (let i = 0; i < keys.length; i++) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      parsedRow[keys[i].trim()] = values[i];
    }
    parsedData.push(parsedRow);
  });
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return parsedData;
};

export const dashInsteadOfNullCommon = (value: Primitives | LocalDate): Primitives | LocalDate => (value ? value : '-');

export const getYearOrSpace = (value: LocalDate): number | string => {
  return value ? DateService.getYear(value) : '';
};

export const spaceInsteadOfNull = (value: Primitives): Primitives => (value ? value : '');

export const safeDivide = (a: number, b: number): number | string => {
  if (a && b && b !== 0) {
    return a / b;
  }
  return '';
};

export const getStatusForSubTotal = (status: string): string => (status.toLowerCase() === 'sold' ? 'realized' : status);

export const formatDate = (date: LocalDate): string => {
  const dateObj = new Date(date.toString());
  const month = dateObj.toLocaleString('default', { month: 'short' });
  const year = dateObj.getFullYear();
  return `${month}, ${year}`;
};

